import {
  formatCurrency,
  formatDate,
  formatPercent,
  formatTradeResult,
  parseNumber,
} from './strategyFormatters';

export function getTradePnl(trade) {
  return parseNumber(
    trade?.realized_pnl ??
      trade?.pnl ??
      trade?.profit_loss ??
      trade?.['P/L']
  );
}

export function getTradeCredit(trade) {
  return parseNumber(
    trade?.entry_credit ??
      trade?.credit ??
      trade?.premium
  );
}

export function isClosedTrade(trade) {
  const status = String(trade?.status || '').trim().toUpperCase();

  if (status === 'OPEN') {
    return false;
  }

  return Boolean(
    status === 'CLOSED' ||
      status === 'WIN' ||
      status === 'LOSS' ||
      trade?.close_date ||
      trade?.closed_at ||
      trade?.realized_pnl !== null ||
      trade?.realized_pnl !== undefined ||
      trade?.pnl !== null ||
      trade?.pnl !== undefined
  );
}

export function buildStrategyRecentStats(trades = [], options = {}) {
  const { limit = 25 } = options;

  const closedTrades = trades.filter(isClosedTrade).slice(0, limit);

  const pnlValues = closedTrades.map(getTradePnl);
  const totalPnl = pnlValues.reduce((sum, pnl) => sum + pnl, 0);

  const wins = pnlValues.filter(pnl => pnl > 0);
  const losses = pnlValues.filter(pnl => pnl < 0);

  const winRate =
    closedTrades.length > 0 ? (wins.length / closedTrades.length) * 100 : null;

  const avgWin =
    wins.length > 0
      ? wins.reduce((sum, pnl) => sum + pnl, 0) / wins.length
      : null;

  const avgLoss =
    losses.length > 0
      ? losses.reduce((sum, pnl) => sum + pnl, 0) / losses.length
      : null;

  return [
    {
      label: 'Recent P/L',
      value: formatCurrency(totalPnl, { showSign: true }),
      helper: `${closedTrades.length} closed trades`,
      tone: totalPnl > 0 ? 'positive' : totalPnl < 0 ? 'negative' : 'neutral',
    },
    {
      label: 'Win Rate',
      value: winRate === null ? '—' : formatPercent(winRate),
      helper: `${wins.length} wins / ${losses.length} losses`,
      tone: 'neutral',
    },
    {
      label: 'Avg Win',
      value: avgWin === null ? '—' : formatCurrency(avgWin, { showSign: true }),
      helper: 'Closed winners',
      tone: 'positive',
    },
    {
      label: 'Avg Loss',
      value: avgLoss === null ? '—' : formatCurrency(avgLoss, { showSign: true }),
      helper: 'Closed losers',
      tone: 'negative',
    },
  ];
}

export function buildRecentTradeRows(trades = [], options = {}) {
  const { limit = 10 } = options;

  return trades
    .filter(isClosedTrade)
    .slice(0, limit)
    .map(trade => {
      const pnl = getTradePnl(trade);
      const credit = getTradeCredit(trade);

      return {
        id: trade.id,
        date: formatDate(trade.entry_date || trade.opened_at),
        expiration: formatDate(trade.expiration_date || trade.expiration_day),
        structure: trade.position_type || trade.structure_type || 'Iron Condor',
        credit: credit
          ? formatCurrency(credit, { maximumFractionDigits: 2 })
          : '—',
        result:
          pnl > 0
            ? 'Win'
            : pnl < 0
              ? 'Loss'
              : formatTradeResult(trade.outcome || trade.status),
        pnl: formatCurrency(pnl, { showSign: true }),
        tone: pnl > 0 ? 'positive' : pnl < 0 ? 'negative' : 'neutral',
      };
    });
}